import CheckBox from 'admin/components/atoms/CheckBox';
import DateTime from 'admin/components/atoms/DateTime';
import Label from 'admin/components/atoms/Label';
import Labeled from 'admin/components/atoms/Labeled';
import NumberInput from 'admin/components/atoms/NumberInput';
import Select from 'admin/components/atoms/Select';
import TextBox from 'admin/components/atoms/TextBox';
import Tooltip from 'admin/components/atoms/Tooltip';
import { isEmptyObj } from 'admin/utils';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { TOPUP_METHODS } from '..';
import { useAppSelector } from '../../../../hooks/useApp';
import { selectUserPrivateMoney } from '../../../../reducers/user-private-money';
import LocalizedMask from '../../../atoms/LocalizedMask';
import Required from '../../../atoms/Required';
import useShopSelector from '../hooks/useShopSelector';
import SenderSelector from '../SenderSelector';

const MySelect = styled(Select)`
  width: 300px;
  margin-right: 10px;
`;

const Disabler = styled.div`
  position: relative;
  ${({ disabled }) =>
    disabled &&
    `
    opacity: 0.4;
    pointer-events: none;
  `};
`;

const FormWrapper = styled.div`
  ${Labeled} {
    display: grid;
    grid-template-columns: 1fr 4fr;
    grid-template-rows: auto;
    justify-items: left;
    margin-top: 10px;
    align-items: center;
    border-bottom: 1px solid #ddd;
    padding: 10px 15px;
  }
  ${Labeled} > ${Label} {
    grid-column: 1;
  }
  ${Select} {
    width: 300px;
    margin-bottom: 10px;
  }
  .amountArrayInput {
    width: 200px;
  }
  ${NumberInput} {
    margin-bottom: 10px;
  }
  ${CheckBox} {
    margin-bottom: 10px;
  }
  ${DateTime} {
    margin-bottom: 0;
  }
`;

const InputRow = styled.div`
  display: flex;
  gap: 16px;
  ${NumberInput}, ${DateTime} {
    width: 250px;
    margin-bottom: 0;
  }
`;

const VerticalLabeled = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const RadioButtonsWrapper = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  margin: 0 5rem 0.25rem 0;

  > div > input {
    margin-right: 0.25rem;
  }
`;

const TopAlignedLabeled = styled(Labeled)`
  align-items: start !important;
`;

const InfoText = styled.span`
  ${({ theme }) => css`
    ${theme.typography.body2.regular}
    color: ${theme.colors.gray[300]};
  `}
`;

const TOOLTIPS = {
  sender: '送金元として設定する店舗',
  method: 'ユーザのアプリ上での受取操作の有無を選択可能',
  reason: (
    <span>
      直接付与方式の場合、お客様の利用履歴に表示するメッセージ（32文字）です
      <br />
      直接付与方式(CSVインポート)の場合、お客様の利用履歴に表示するメッセージ
      <br />
      はCSVファイル内で指定します
    </span>
  ),
  amount: 'ユーザへ送金する金額の指定',
  bearShop:
    'ポイント利用分をマネー発行者に支払う店舗を指定します。未指定時はマネー発行者が負担します。',
  expiryAbsolute: '付与ポイントの有効期限を絶対日で指定',
  expiryRelative: '付与ポイントの有効期限を受取日からの相対日数で指定',
  executionMode: '付与を実行するタイミング',
  pointExpiry: (
    <span>
      未指定の場合はマネー有効期限と同一の有効期限が設定されます。
      <br />
      日付指定と相対日数指定が両方指定されている場合は、受け取り時点でより近い方が有効期限に設定されます。
    </span>
  ),
};

const parseNumberOrDefault = (value, defaultValue) => {
  const parsedValue = parseInt(value, 10);
  return Number.isNaN(parsedValue) ? defaultValue : parsedValue;
};

const parseNumberOrZero = (value) => parseNumberOrDefault(value, 0);

const TooltipLabeled = ({ labelText, id, tooltipText, required, topAlign, children, ...rest }) => {
  const LabeledComponent = topAlign ? TopAlignedLabeled : Labeled;
  return (
    <LabeledComponent
      labelText={
        <div>
          {labelText} {required ? <Required /> : null}
          <Tooltip id={id} text={tooltipText} />
        </div>
      }
      {...rest}
    >
      {children}
    </LabeledComponent>
  );
};

const BearPointAccountSelector = ({ bearPointAccount, onSelectBearPointAccount }) => {
  const userPrivateMoney = useAppSelector(selectUserPrivateMoney);
  const [selectedOrganization, setSelectedOrganization] = useState({});

  const [organizationOptions, shopOptions] = useShopSelector({
    money: userPrivateMoney,
    organization: selectedOrganization,
  });

  useEffect(() => {
    setSelectedOrganization({});
    onSelectBearPointAccount({});
  }, [userPrivateMoney]);

  const toOption = (value) => (value && !isEmptyObj(value) ? { label: value.name, value } : null);

  const onChangeOrganization = ({ value: organization }) => {
    setSelectedOrganization(organization);
    onSelectBearPointAccount({});
  };

  const onChangeShop = ({ value: shop }) => {
    onSelectBearPointAccount(shop);
  };

  return (
    <div>
      <MySelect
        options={organizationOptions}
        onChange={onChangeOrganization}
        placeholder="「組織」を選択"
        value={toOption(selectedOrganization)}
      />
      <MySelect
        options={shopOptions}
        onChange={onChangeShop}
        placeholder="「店舗」を選択"
        value={toOption(bearPointAccount)}
      />
    </div>
  );
};

const RewardSettingForm = ({
  rewardSettings,
  setRewardSettings,
  isSavedReward,
  onChangeSenderShop,
}) => {
  const {
    privateMoney,
    senderOrganization,
    senderShop,
    topupMethod,
    moneyAmount,
    pointAmount,
    pointExpiryDate,
    relativePointExpiryDays,
    bearPointAccount,
    description,
  } = rewardSettings;

  const moneyRef = useRef();
  const pointRef = useRef();
  const expiryDaysRef = useRef();
  const descriptionRef = useRef();

  useEffect(() => {
    if (!moneyRef || !moneyRef.current) return;
    moneyRef.current.value = moneyAmount;
  }, [moneyAmount]);

  useEffect(() => {
    if (!pointRef || !pointRef.current) return;
    pointRef.current.value = pointAmount;
  }, [pointAmount]);

  useEffect(() => {
    if (!expiryDaysRef || !expiryDaysRef.current) return;
    expiryDaysRef.current.value = relativePointExpiryDays;
  }, [relativePointExpiryDays]);

  useEffect(() => {
    if (!descriptionRef || !descriptionRef.current) return;
    descriptionRef.current.value = description;
  }, [description]);

  const onChangeSetting = (newSetting) => {
    setRewardSettings((settings) => ({
      ...settings,
      ...newSetting,
    }));
  };

  return (
    <FormWrapper>
      <TooltipLabeled
        labelText="付与方法"
        id="method"
        tooltipText={TOOLTIPS.method}
        required
        topAlign
      >
        <div>
          <RadioButtonsWrapper>
            {[
              {
                label: '受取リクエスト(ユーザ操作あり)',
                value: TOPUP_METHODS.collectable,
              },
              { label: '直接付与方式', value: TOPUP_METHODS.direct },
              { label: '直接付与方式(CSVインポート)', value: TOPUP_METHODS.bulk },
            ].map(({ label, value }) => (
              <div key={`${value}-method`}>
                <input
                  type="radio"
                  name="operation-method"
                  id={`${value}-method`}
                  value={value}
                  checked={topupMethod === value}
                  onChange={() => onChangeSetting({ topupMethod: value })}
                />
                <label htmlFor={`${value}-method`}>{label}</label>
              </div>
            ))}
          </RadioButtonsWrapper>
          {topupMethod !== TOPUP_METHODS.collectable && (
            <>
              <div>
                <label>付与事由</label>
                <Tooltip id="reason" text={TOOLTIPS.reason} />
              </div>
              <TextBox
                fullWidth
                value={description}
                innerRef={descriptionRef}
                maxLength={32}
                style={{ width: '100%', maxWidth: '750px' }}
                onChange={(e) => {
                  const { value } = e.target;
                  if (value.length > 32) return;
                  onChangeSetting({ description: value });
                }}
              />
            </>
          )}
        </div>
      </TooltipLabeled>
      <TooltipLabeled labelText="送金元店舗" id="sender" tooltipText={TOOLTIPS.sender} required>
        <SenderSelector
          privateMoney={privateMoney}
          senderOrganization={senderOrganization}
          senderShop={senderShop}
          onSelectPrivateMoney={(money) => onChangeSetting({ privateMoney: money })}
          onSelectOrganization={(org) => onChangeSetting({ senderOrganization: org })}
          onSelectShop={onChangeSenderShop}
        />
      </TooltipLabeled>
      {topupMethod !== TOPUP_METHODS.bulk && (
        <Labeled labelText="金額" id="amount" required>
          <div>
            <InputRow>
              <VerticalLabeled>
                <span>マネー</span>
                <LocalizedMask
                  value={moneyAmount}
                  onChange={(e) => {
                    const { value } = e.target;
                    onChangeSetting({ moneyAmount: parseNumberOrZero(value) });
                  }}
                >
                  {(inputProps) => (
                    <NumberInput
                      id="money_amount_input"
                      {...inputProps}
                      innerRef={moneyRef}
                      min="0"
                    />
                  )}
                </LocalizedMask>
              </VerticalLabeled>
              <VerticalLabeled>
                <span>ポイント</span>
                <LocalizedMask
                  value={pointAmount}
                  onChange={(e) => {
                    const { value } = e.target;
                    onChangeSetting({ pointAmount: parseNumberOrZero(value) });
                  }}
                >
                  {(inputProps) => (
                    <NumberInput
                      {...inputProps}
                      id="point_amount_input"
                      innerRef={pointRef}
                      min="0"
                    />
                  )}
                </LocalizedMask>
              </VerticalLabeled>
            </InputRow>
            <InfoText>マネーあるいはポイントいずれかの入力が必須です</InfoText>
          </div>
        </Labeled>
      )}
      <Disabler
        disabled={topupMethod !== TOPUP_METHODS.bulk && (pointAmount === 0 || isSavedReward)}
      >
        <TooltipLabeled
          labelText="ポイント有効期限"
          id="pointExpiry"
          tooltipText={TOOLTIPS.pointExpiry}
        >
          <InputRow>
            <VerticalLabeled>
              <span>日時指定</span>
              <DateTime
                showIcon
                value={pointExpiryDate}
                validType="validAfterToday"
                timeFormat={false}
                dateFormat="YYYY/MM/DD HH:mm:ss"
                onChange={(date) => {
                  const endOfDate = date ? date.endOf('day') : undefined;
                  onChangeSetting({ pointExpiryDate: endOfDate });
                }}
              />
            </VerticalLabeled>
            <VerticalLabeled>
              <span>相対日数指定</span>
              <NumberInput
                id="point_limit_relative"
                value={relativePointExpiryDays || ''}
                innerRef={expiryDaysRef}
                min="1"
                placeholder="なし"
                onChange={(e) => {
                  const { value } = e.target;
                  const parsedValue = parseNumberOrZero(value);
                  const validated = parsedValue > 0 ? parsedValue : undefined;
                  onChangeSetting({ relativePointExpiryDays: validated });
                }}
              />
              <span style={{ color: '#757575', marginTop: '0.25rem' }}>
                1にすると当日の24時までになります
              </span>
            </VerticalLabeled>
          </InputRow>
        </TooltipLabeled>
      </Disabler>
      {(topupMethod === TOPUP_METHODS.direct || topupMethod === TOPUP_METHODS.bulk) && (
        <Disabler
          disabled={topupMethod === TOPUP_METHODS.direct && (pointAmount === 0 || isSavedReward)}
        >
          <TooltipLabeled
            labelText="ポイント負担先店舗"
            id="bearShop"
            tooltipText={TOOLTIPS.bearShop}
          >
            <BearPointAccountSelector
              bearPointAccount={bearPointAccount}
              onSelectBearPointAccount={(account) => {
                onChangeSetting({ bearPointAccount: account });
              }}
            />
          </TooltipLabeled>
        </Disabler>
      )}
    </FormWrapper>
  );
};

TooltipLabeled.propTypes = {
  id: PropTypes.string,
  labelText: PropTypes.string.isRequired,
  tooltipText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  children: PropTypes.node.isRequired,
  required: PropTypes.bool,
  topAlign: PropTypes.bool,
};

TooltipLabeled.defaultProps = {
  id: undefined,
  required: false,
  topAlign: false,
};

BearPointAccountSelector.propTypes = {
  bearPointAccount: PropTypes.object,
  onSelectBearPointAccount: PropTypes.func.isRequired,
};

BearPointAccountSelector.defaultProps = {
  bearPointAccount: {},
};

RewardSettingForm.propTypes = {
  rewardSettings: PropTypes.shape({
    privateMoney: PropTypes.object,
    senderOrganization: PropTypes.object,
    senderShop: PropTypes.object,
    topupMethod: PropTypes.string,
    moneyAmount: PropTypes.number,
    pointAmount: PropTypes.number,
    pointExpiryDate: PropTypes.object,
    relativePointExpiryDays: PropTypes.number,
    isReserve: PropTypes.bool,
    bearPointAccount: PropTypes.object,
    description: PropTypes.string,
  }).isRequired,
  setRewardSettings: PropTypes.func.isRequired,
  isSavedReward: PropTypes.bool.isRequired,
  onChangeSenderShop: PropTypes.func.isRequired,
};

export default RewardSettingForm;
